/**
 * Typed fetchers for support tickets — escalating a question Explore could not
 * answer (`/api/v1/ai/chats/{chat}/messages/{message}/ticket`), the caller's
 * own tickets, and the Administrator queue (`/api/v1/admin/tickets*`).
 *
 * Types come from `./_generated/schema` (CLAUDE.md §12.8). Regenerate with
 * `pnpm gen:api` after any change to `backend/src/daquma/api/v1/tickets.py`.
 */

import type { components } from "./_generated/schema";
import { apiFetch } from "./apiFetch";

export type TicketOut = components["schemas"]["TicketOut"];
export type TicketListOut = components["schemas"]["TicketListOut"];
export type RaiseTicketOut = components["schemas"]["RaiseTicketOut"];
export type UpdateTicketIn = components["schemas"]["UpdateTicketIn"];
export type TicketStatus = TicketOut["status"];

export const TICKET_STATUSES: readonly TicketStatus[] = [
  "open",
  "in_progress",
  "resolved",
  "closed",
] as const;

/** Raise a ticket from a refused answer (chat owner only). Idempotent: a second
 *  call returns the existing ticket with `created: false`. */
export function raiseTicket(
  token: string,
  chatId: string,
  messageId: string,
): Promise<RaiseTicketOut> {
  return apiFetch<RaiseTicketOut>(
    `/api/v1/ai/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/ticket`,
    { method: "POST", token },
  );
}

export function listMyTickets(token: string): Promise<TicketListOut> {
  return apiFetch<TicketListOut>("/api/v1/tickets/mine", { token });
}

export function listTickets(token: string, status?: TicketStatus | null): Promise<TicketListOut> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<TicketListOut>(`/api/v1/admin/tickets${query}`, { token });
}

export function getTicket(token: string, ticketId: string): Promise<TicketOut> {
  return apiFetch<TicketOut>(`/api/v1/admin/tickets/${encodeURIComponent(ticketId)}`, { token });
}

/** Change a ticket's status (Administrator). Audited server-side as
 *  `ticket.status_changed`. */
export function updateTicket(
  token: string,
  ticketId: string,
  input: UpdateTicketIn,
): Promise<TicketOut> {
  return apiFetch<TicketOut>(`/api/v1/admin/tickets/${encodeURIComponent(ticketId)}`, {
    method: "PATCH",
    token,
    json: input,
  });
}
